/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import Msdyn365, { ContentEditableEvent, generateImageUrl, getCatalogId, getUrlSync, Image, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { MediaLocation, ProductCatalog } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import React, { ReactNode } from 'react';

import { ICatalogPickerData } from './catalog-picker.data';
import { ICatalogPickerProps } from './catalog-picker.props.autogenerated';

/**
 * ICatalogPickerViewProps.
 */
export interface ICatalogPickerViewProps extends ICatalogPickerProps<ICatalogPickerData> {
    catalogPicker: IModuleProps;
    heading: React.ReactNode;
    paragraph: React.ReactNode;
    catalogs: ReactNode;
}

/**
 * CatalogPicker component.
 * @extends {React.PureComponent<ICatalogPickerProps<ICatalogPickerConfig>>}
 */
export class CatalogPicker extends React.PureComponent<ICatalogPickerProps<ICatalogPickerData>> {
    private static readonly _moduleClassName = 'ms-catalog-picker';

    public constructor(props: ICatalogPickerViewProps) {
        super(props);
    }

    public render(): JSX.Element | null {
        const {
            heading,
            paragraph
        } = this.props.config;

        const catalogPickerHeading = heading && (
            <Text
                className={`${CatalogPicker._moduleClassName}__list__heading`}
                tag={heading.tag ?? 'h1'}
                text={heading.text}
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );

        const isCatalogsAvailable: boolean = ArrayExtensions.hasElements(this.props.data.catalogs?.result);
        const catalogPickerParagraph = paragraph && (
            <RichTextComponent
                className={`${CatalogPicker._moduleClassName}__list__subheading`}
                text={isCatalogsAvailable ? paragraph : this.props.resources.catalogValidationText}
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );
        const viewProps = {
            ...this.props,
            handleText: this.handleTextChange,
            catalogPicker: {
                moduleProps: this.props,
                className: CatalogPicker._moduleClassName
            },
            heading: catalogPickerHeading,
            paragraph: catalogPickerParagraph,
            catalogs: this._renderCatalogPicker()
        };

        return this.props.renderView(viewProps);
    }

    /**
     * Handle text change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleTextChange = (event: ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handle paragraph change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleParagraphChange = (event: ContentEditableEvent): void => {
        this.props.config.paragraph = event.target.value;
    };

    private _renderCatalogPickerButton(item: ProductCatalog): React.ReactNode {
        const catalogId = item.RecordId;
        const currentCatalogId = getCatalogId(this.props.context.request);
        if (currentCatalogId === catalogId) {
            return (
                <div className={`${CatalogPicker._moduleClassName}__current-catalog`}>
                    {this.props.resources.currentCatalogText}
                </div>
            );
        }
        let linkToCatalog = '';
        if (Msdyn365.isBrowser) {
            const catalogUrl = getUrlSync('home', this.props.context.actionContext) ?? '';
            const fullUrl = new URL(catalogUrl, window.location.href);
            fullUrl.searchParams.set('catalogid', `${catalogId}`);
            linkToCatalog = fullUrl.href;
        }
        return (
            <Button
                className={`${CatalogPicker._moduleClassName}__button`}
                aria-label={this.props.resources.changeCatalogButtonText}
                href={linkToCatalog}
            >
                {this.props.resources.changeCatalogButtonText}
            </Button>
        );
    }

    private _renderCatalogPicker(): React.ReactNode {
        const catalogs = this.props.data.catalogs?.result;

        const isBrowser = Msdyn365.isBrowser;

        return (
            <div className={`${CatalogPicker._moduleClassName}__list__wrapper`}>
                {
                    catalogs?.map(item => {
                        const reactKey = `${JSON.stringify(item)}-${String(isBrowser)}`;
                        const image: MediaLocation | undefined = ArrayExtensions.firstOrDefault(item.Images);
                        return (
                            <div
                                key={reactKey}
                                className={`${CatalogPicker._moduleClassName}__list__item__wrapper`}
                                tabIndex={0}
                                aria-label={item.Name}
                                role='menuitem'
                            >
                                <h1 className={`${CatalogPicker._moduleClassName}__list__item__text`}>
                                    {
                                        item.Name
                                    }
                                </h1>
                                <div className={`${CatalogPicker._moduleClassName}__list__item__section`}>
                                    <Image
                                        requestContext={this.props.context.request}
                                        className={`${CatalogPicker._moduleClassName}__list__item__img`}
                                        key={reactKey}
                                        src={generateImageUrl(this.props.context.request.apiSettings, image?.Uri)}
                                        gridSettings={this.props.context.request.gridSettings!}
                                        imageSettings={this.props.config.galleryImageSettings}
                                        altText={image?.AltText}
                                        loadFailureBehavior='hide'
                                    />
                                    <div className={`${CatalogPicker._moduleClassName}__button__wrapper`}>
                                        {this._renderCatalogPickerButton(item)}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                }
            </div>
        );
    }
}

export default CatalogPicker;
